// Lightweight clearance chip used in attendance/roster surfaces.
// Tap reveals the blocker list and a "Resolve" link that routes the
// caller to the right correction surface.
//
// Renders nothing if the child has no blockers AND no `forceVisible`.

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Warning, X, ArrowRight } from "@phosphor-icons/react";
import { fetchChildClearance, CLEARANCE_CHIP } from "@/lib/dailyOpsCompliance";

export default function ClearanceChip({
  childId,
  resolveHref,         // where the "Resolve" CTA should route
  resolveLabel = "Resolve",
  forceVisible = false,
  inline = true,
  testIdPrefix = "clearance",
}) {
  const [state, setState] = useState({ loading: true, cleared: true, blockers: [] });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    if (!childId) return;
    setState((s) => ({ ...s, loading: true }));
    fetchChildClearance(childId)
      .then((r) => alive && setState({ loading: false, ...r }))
      .catch(() => alive && setState({ loading: false, cleared: true, blockers: [] }));
    return () => { alive = false; };
  }, [childId]);

  const meta = useMemo(() => {
    if (state.loading) return CLEARANCE_CHIP.unknown;
    return state.cleared ? CLEARANCE_CHIP.cleared : CLEARANCE_CHIP.action_needed;
  }, [state]);

  // Default behavior: hide cleared chips unless forced.
  if (!forceVisible && !state.loading && state.cleared) return null;

  const Wrapper = inline ? "span" : "div";

  return (
    <Wrapper className="relative inline-flex items-center">
      <button
        type="button"
        data-testid={`${testIdPrefix}-${childId}`}
        data-cleared={state.cleared ? "true" : "false"}
        data-blocker-count={state.blockers.length}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen((v) => !v); }}
        aria-haspopup="dialog"
        aria-expanded={open}
        className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${meta.tone}`}
      >
        {state.cleared ? (
          <ShieldCheck size={10} weight="duotone" />
        ) : (
          <Warning size={10} weight="duotone" />
        )}
        {meta.label}
      </button>

      {open && !state.cleared && state.blockers.length > 0 && (
        <div
          data-testid={`${testIdPrefix}-${childId}-popover`}
          role="dialog"
          className="absolute right-0 top-full z-30 mt-1 w-64 rounded-2xl border border-neutral-200 bg-white p-3 text-left shadow-lg"
        >
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-500">Action needed</p>
            <button
              type="button"
              data-testid={`${testIdPrefix}-${childId}-close`}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(false); }}
              className="rounded-full p-1 text-neutral-500 hover:bg-neutral-100"
              aria-label="Close"
            >
              <X size={11} weight="bold" />
            </button>
          </div>
          <ul className="mt-2 space-y-1.5" data-testid={`${testIdPrefix}-${childId}-popover-list`}>
            {state.blockers.slice(0, 5).map((b, i) => {
              const fixHref = resolveHref
                ? `${resolveHref}?childId=${encodeURIComponent(childId)}${b.typeId ? `&openType=${encodeURIComponent(b.typeId)}` : ""}`
                : null;
              return (
                <li
                  key={`${b.typeId || b.recordId || i}`}
                  data-testid={`${testIdPrefix}-${childId}-popover-blocker-${b.typeId || i}`}
                  className="text-[12px] text-neutral-700"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-semibold text-neutral-900">{b.typeLabel}</p>
                      <p className="text-[11px] text-neutral-500">{b.reason}</p>
                    </div>
                    {fixHref && (
                      <Link
                        to={fixHref}
                        data-testid={`${testIdPrefix}-${childId}-popover-fix-${b.typeId || i}`}
                        onClick={() => setOpen(false)}
                        className="shrink-0 rounded-full bg-primary px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white hover:bg-primary-hover"
                      >
                        Fix
                      </Link>
                    )}
                  </div>
                </li>
              );
            })}
            {state.blockers.length > 5 && (
              <li className="text-[11px] italic text-neutral-500">+{state.blockers.length - 5} more</li>
            )}
          </ul>
          {resolveHref && (
            <Link
              to={resolveHref}
              data-testid={`${testIdPrefix}-${childId}-resolve`}
              onClick={() => setOpen(false)}
              className="mt-3 inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1 text-[11px] font-semibold text-white hover:bg-primary-hover"
            >
              {resolveLabel}
              <ArrowRight size={11} weight="bold" />
            </Link>
          )}
        </div>
      )}
    </Wrapper>
  );
}
